import React from 'react';
import { StyleSheet, View } from 'react-native';
import { AppText } from '../AppText';
import Colors from '../../constants/Colors';
import { Event } from '../../types/Site';
import { useTranslation } from '../../providers/TranslationProvider';
import { BookingBadgeVariant, bookingBadgeVariant, bookingModeTranslationKey } from './siteFormatting';
import { DateChip, DateChipVariant } from './DateChip';

const styles = StyleSheet.create({
  container: {
    gap: 8,
  },
  withDivider: {
    borderTopWidth: 1,
    borderTopColor: Colors.lightGrayBackground,
    paddingTop: 12,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  name: {
    fontSize: 15,
    fontWeight: '600',
    color: Colors.veryDarkGray,
  },
  badge: {
    height: 22,
    paddingHorizontal: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  walkInBadge: {
    borderRadius: 11,
    backgroundColor: Colors.siteListPaleGreenBg,
  },
  reservationBadge: {
    borderRadius: 4,
    borderWidth: 1,
    borderColor: Colors.siteListReservationBorder,
    backgroundColor: Colors.siteListReservationBg,
  },
  preRegistrationBadge: {
    borderRadius: 4,
    borderWidth: 1,
    borderColor: Colors.siteListPreRegistrationBorder,
    backgroundColor: Colors.siteListPreRegistrationBg,
  },
  unknownBadge: {
    borderRadius: 4,
    borderWidth: 1,
    borderColor: Colors.grayBorder,
    backgroundColor: Colors.lightGrayBackground,
  },
  badgeText: {
    fontSize: 13,
    fontWeight: '700',
  },
  walkInBadgeText: { color: Colors.darkGreen },
  reservationBadgeText: { color: Colors.siteListReservationText },
  preRegistrationBadgeText: { color: Colors.siteListPreRegistrationText },
  unknownBadgeText: { color: Colors.mediumGray },
  chipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  moreChip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 5,
    paddingHorizontal: 10,
    borderRadius: 8,
    backgroundColor: Colors.lightGrayBackground,
  },
  moreChipText: {
    fontSize: 13,
    fontWeight: '700',
    color: Colors.darkGreen,
  },
});

export interface EventBlockProps {
  event: Event;
  now: Date;
  showDivider: boolean;
  timezone?: string;
}

const BADGE_STYLE_BY_VARIANT: Record<BookingBadgeVariant, object> = {
  walkIn: styles.walkInBadge,
  reservation: styles.reservationBadge,
  preRegistration: styles.preRegistrationBadge,
  unknown: styles.unknownBadge,
};

const BADGE_TEXT_STYLE_BY_VARIANT: Record<BookingBadgeVariant, object> = {
  walkIn: styles.walkInBadgeText,
  reservation: styles.reservationBadgeText,
  preRegistration: styles.preRegistrationBadgeText,
  unknown: styles.unknownBadgeText,
};

// `index` is into the already-filtered, opensAt-sorted window list, so index 0 is always the
// soonest window that's still open or upcoming -- elapsed windows never reach this function.
function chipVariantFor(variant: BookingBadgeVariant, index: number): DateChipVariant {
  if (variant !== 'walkIn') return variant === 'unknown' ? 'neutral' : variant;
  return index === 0 ? 'soonestWalkIn' : 'neutral';
}

function currentAndFutureWindows(event: Event, now: Date) {
  return [...(event.windows ?? [])]
    .filter((window) => new Date(window.closesAt).getTime() >= now.getTime())
    .sort((a, b) => new Date(a.opensAt).getTime() - new Date(b.opensAt).getTime());
}

// Single source of truth for "does this event have anything to show a neighbor" -- shared with
// SiteListItem so it can compute showDivider off the same set of events EventBlock actually
// renders, rather than the raw (pre-filter) events array drifting out of sync with this check.
export function eventHasVisibleContent(event: Event, now: Date): boolean {
  return currentAndFutureWindows(event, now).length > 0 || event.hasMoreWindows;
}

export function EventBlock({ event, now, showDivider, timezone }: EventBlockProps) {
  const { t, locale } = useTranslation();
  const variant = bookingBadgeVariant(event.bookingMode);
  // windows is required per the Event type, but this is still-stabilizing API data -- fall back
  // to an empty list rather than throwing if a real response omits it (matches the prior
  // SiteListItem behavior this component replaces).
  // Elapsed windows are dropped entirely rather than rendered unhighlighted -- otherwise a window
  // that already closed still reads as "Today 10-1 PM" directly under a pill that may say the
  // site has no upcoming services, which a neighbor can misread as still available.
  const sortedWindows = currentAndFutureWindows(event, now);
  const todayLabel = t('site_list_today');
  const tomorrowLabel = t('site_list_tomorrow');

  // Nothing left to tell a neighbor about this event -- every window has elapsed and there's no
  // "+ more times" chip to point them toward either.
  if (!eventHasVisibleContent(event, now)) {
    return null;
  }

  return (
    <View
      style={[styles.container, showDivider && styles.withDivider]}
      testID={`event-block-${event.eventId}`}
    >
      <View style={styles.headerRow}>
        <AppText style={styles.name}>{event.name || t('site_list_default_event_name')}</AppText>
        <View style={[styles.badge, BADGE_STYLE_BY_VARIANT[variant]]}>
          <AppText style={[styles.badgeText, BADGE_TEXT_STYLE_BY_VARIANT[variant]]}>
            {t(bookingModeTranslationKey(variant))}
          </AppText>
        </View>
      </View>
      <View style={styles.chipRow}>
        {sortedWindows.map((window, index) => (
          <DateChip
            key={window.occurrenceId}
            window={window}
            // reservation and preRegistration both put every chip in their own treatment (each
            // occurrence requires the same action); walkIn highlights the soonest (index 0, since
            // elapsed windows are already filtered out) chip over later neutral ones; unknown is
            // always neutral.
            variant={chipVariantFor(variant, index)}
            now={now}
            todayLabel={todayLabel}
            tomorrowLabel={tomorrowLabel}
            timezone={timezone}
            locale={locale}
          />
        ))}
        {event.hasMoreWindows && (
          <View style={styles.moreChip} testID={`event-more-${event.eventId}`}>
            <AppText style={styles.moreChipText}>{t('site_list_more_times')}</AppText>
          </View>
        )}
      </View>
    </View>
  );
}
